import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import DoneIcon from '@mui/icons-material/Done';

/**
 * Reusable purchase / payment success screen used within Ad Management.
 *
 * Props:
 *  - title:            heading message shown next to the success icon.
 *  - subtitle:         optional supporting text below the title.
 *  - details:          array of { label, value } pairs to render as a summary.
 *  - primaryButton:    { label, onClick } for the main CTA (e.g. "Go to Ad List").
 *  - secondaryButton:  optional { label, onClick } for an additional action.
 *  - icon:             optional custom icon node (defaults to a check icon).
 */
const PurchaseSuccess = ({
  title,
  subtitle,
  details = [],
  primaryButton,
  secondaryButton,
  icon,
}) => {

    const isSuccessColor = (value) => {
        switch (value) {
          case "Status":
          case "Amount Charged":
            return "#16a34a";
          default:
            return "inherit";
        }
    }
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "calc(100vh - 120px)",
        px: 2,
        py: 4,
        backgroundColor: (theme) => theme.palette.background.default,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 520,
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
          textAlign: "center",
        }}
      >
        <Stack spacing={2} alignItems="center">
          <Box
            sx={{
              width: 88,
              height: 88,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: (theme) => theme.palette.success.light + "33",
            }}
          >
            {icon || (
              <DoneIcon
                color="success"
                sx={{ fontSize: 64, color: "#16a34a" }}
                aria-hidden="true"
              />
            )}
          </Box>

          <Typography variant="h5" component="h1" fontWeight={600}>
            {title}
          </Typography>

          {subtitle && (
            <Typography variant="body1" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Stack>

        {details.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Stack spacing={1.5} sx={{ textAlign: "left" }}>
              {details.map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ flexShrink: 0 }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ textAlign: "right", wordBreak: "break-word", color: isSuccessColor(item.label) }}
                  >
                    {item.value ?? "-"}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </>
        )}

        {(primaryButton || secondaryButton) && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            sx={{ mt: 4 }}
          >
            {secondaryButton && (
              <Button
                variant="outlined"
                color="primary"
                onClick={secondaryButton.onClick}
                fullWidth
              >
                {secondaryButton.label}
              </Button>
            )}
            {primaryButton && (
              <Button
                variant="contained"
                color="primary"
                onClick={primaryButton.onClick}
                fullWidth
              >
                {primaryButton.label}
              </Button>
            )}
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

PurchaseSuccess.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  details: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node]),
    })
  ),
  primaryButton: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }),
  secondaryButton: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }),
  icon: PropTypes.node,
};

export default PurchaseSuccess;
